import React, { useEffect, useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import {
  getSelectedStore,
  getStores,
  selectStore,
  setEvents,
  useAppContext,
  getStoreBySlug,
  setBreadcrumbs,
  setError,
  selectEvent,
  getStoreEventsWithFilters,
  getStoreEvents,
} from '../../contextProviders/AppContext';
import StoreSelector from '../../components/StoreSelector';
import { getEvents } from '../../services/storeEventApi';
import { Breadcrumb, CSSEvent, Store, StoreMap } from '../../types/model';
import { Page } from '../../types/Seo';
import ConditionalRender from '../../components/ConditionalRender';
import Seo from '../../components/Seo/Seo';
import { EVENT_LIST, EVENT_STORE_LIST } from '../../components/Seo/enums';
import analytics from '../../analytics';
import useScrollToTop from '../../hooks/ScrollToTop';
import Focus from '../../components/Focus';
import NoStoreSelected from './NoStoreSelected';
import StoreSelected from './StoreSelected';
import Divider from '../../components/Divider';
import { getURLParameters } from '../../utils/url';
import { useEventFilters } from '../../hooks/useEventFilters';
import { PillButtonComponent } from '../../components/PillButtonComponent';
import { RemovePillButtonCompnent } from '../../components/RemovePillButtonComponent';
import LoadingIndicator from '../../components/LoadingIndicator';
import { useQuery } from 'react-query';
import UpcomingEvent from './LiveBroadcast';

const StoreSelection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: var(--space-200);
  > div:first-child {
    margin: 0 0 var(--space-200) 0;
  }

  ${({ theme }) => `${theme.breakpoints.lg} {
    flex-direction: row;
    margin-bottom: var(--space-400);
    > div:first-child {
      margin: 0;
      margin-inline-end: var(--space-300);
    }
  }`};
`;

const Title = styled.h1`
  margin-bottom: var(--space-200);
  ${({ theme }) => `${theme.breakpoints.lg} {
    margin-bottom: var(--space-250);
  }`};
`;

const Events = () => {
  const { useSelector, dispatch } = useAppContext();
  const { storeName } = useParams<{ storeName: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const stores: StoreMap = useSelector(getStores);
  const selectedStore: Store = useSelector(getSelectedStore);
  const urlStore: Store = useSelector(getStoreBySlug(storeName));
  const [pageName, setPageName] = useState<Page>(EVENT_LIST);
  const filterUrlParam = getURLParameters('filter')?.toString();
  const { filterEventState, updateFilter, resetFilter } =
    useEventFilters(filterUrlParam);
  const events: Array<CSSEvent> = useSelector(
    filterEventState && filterEventState.length > 0
      ? getStoreEventsWithFilters(filterEventState)
      : getStoreEvents()
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [otherstoreName, setOtherstoreName] = useState<string | undefined>();

  const queryParams = location.search;
  useScrollToTop();
  useEffect(() => {
    dispatch(selectEvent(null));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (urlStore) {
      dispatch(selectStore(urlStore.storeNo));
    } else {
      dispatch(selectStore(null));
    }
    const url = window.location.href;
    var path = url.replace(/^https?:\/\//, '').split('/');

    if (selectedStore !== undefined) {
      if (
        ((["jp", "kr"].includes(path[1]) && path[2] === "en") ||
          (path[1] === "ca" && path[2] === "fr"))
      ) {
        setOtherstoreName(selectedStore.storeOtherName);
      } else {
        setOtherstoreName(selectedStore.storeName);
      }

    }
  }, [urlStore, dispatch, otherstoreName, selectedStore]);

  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      const apiEvents = await getEvents(selectedStore?.storeNo || '');
      dispatch(setEvents(apiEvents));
    } catch (e: any) {
      dispatch(setError(e.code));
      Sentry.captureException(e);
    } finally {
      setIsLoading(false);
    }
  };

  const { isLoading: isQueryLoading } = useQuery(
    ['getevents', selectedStore?.storeNo],
    fetchEvents,
    {
      enabled: !!selectedStore?.storeNo && !!urlStore,
      staleTime: 2 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    // if (selectedStore?.storeNo && events.length > 0) {
    //   // setIsLoading(false);
    // } // Not USED FOR NOW

    //stop loadingIndicator when no store is selected
    if (otherstoreName === undefined || otherstoreName === 'all') {
      setIsLoading(false);
    }
  }, [dispatch, selectedStore?.storeNo, otherstoreName, events.length]);

  useEffect(() => {
    if (selectedStore?.storeNo || queryParams) {
      const queryStrings = new URLSearchParams(queryParams);
      analytics.sendPageview({
        page_type: 'events',
        local_store: selectedStore?.storeNo,
        custom: {
          utm_campaign: queryStrings.get('utm_campaign'),
          utm_content: queryStrings.get('utm_content'),
          utm_medium: queryStrings.get('utm_medium'),
          utm_source: queryStrings.get('utm_source'),
          utm_term: queryStrings.get('utm_term')
        }
      });
      // Send event to analytics when the page is loaded
      analytics.sendEvent(
        {
          label: 'store_details',
          event_action: queryStrings.get('event_action') || '',
          custom: { ref_app: queryStrings.get('ref_app') || '', store_id: queryStrings.get('store_id') || '' },
        })
    }
  }, [queryParams, selectedStore?.storeNo]);


  useEffect(() => {
    const breadCrumbs: Breadcrumb[] = [];
    if (otherstoreName !== undefined) {
      breadCrumbs.push({ text: otherstoreName });
      setPageName(EVENT_STORE_LIST);
    }
    dispatch(setBreadcrumbs(breadCrumbs));
  }, [dispatch, otherstoreName]);

  const onSelectStore = (e: React.FormEvent<HTMLSelectElement>) => {
    const target = e.target as HTMLSelectElement;
    const store = stores[target.value];

    if (store) {
      if (filterEventState && filterEventState.length > 0) {
        history.push(
          `/${store.slug}/?filter=${filterEventState
            .join(',')
            ?.toLocaleLowerCase()}`
        );
      } else {
        history.push(`/${store.slug}/`);
      }
    } else {
      history.push('/all');
    }
  };

  const title = selectedStore
    ? t('event.whatsGoingOnIn', {
      storeName: otherstoreName,
      interpolation: { escapeValue: false },
    })
    : t('event.allEventsInCountry');

  return (
    <Focus>
      <Seo page={pageName} canonicalPath={location.pathname} />
      <Title>{title}</Title>
      <StoreSelection>
        <StoreSelector onChange={onSelectStore} selectedStore={selectedStore} />
        <PillButtonComponent
          filterEventState={filterEventState}
          updateFilter={updateFilter}
        />
      </StoreSelection>
      <ConditionalRender
        predicate={isLoading || isQueryLoading}
        fallback={
          <>
            <Divider />
            <RemovePillButtonCompnent
              filterEventState={filterEventState}
              updateFilter={updateFilter}
              resetFilter={resetFilter}
            />

            <ConditionalRender
              predicate={Boolean(selectedStore)}
              fallback={<NoStoreSelected />}
            >
              <StoreSelected
                events={events}
                selectedStore={selectedStore}
                numberOfActiveFilters={filterEventState.length}
              />
            </ConditionalRender>
            {(storeName === undefined || storeName === 'all') ?
              <div>
                <Divider />
                <ConditionalRender
                  predicate={Boolean(selectedStore)}
                  fallback={<UpcomingEvent />}
                >
                  {/* Add a placeholder element as the children */}
                  <div></div>
                </ConditionalRender> </div>
              : <div></div>
            }
          </>
        }
      >
        <LoadingIndicator />
      </ConditionalRender>
    </Focus>
  );
};

export default Events;